import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css"; // Import Bootstrap CSS
import { useNav } from "../../components/NavContext";

const DoctorList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [failedImages, setFailedImages] = useState(new Set()); // track broken images to avoid flicker
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, per_page: 10, total: 0 });
  const DOMAIN = process.env.REACT_APP_DOMAIN;
  const API_URL = process.env.REACT_APP_API_URL;
  const DOCTOR_PROFILE_PICTURE_URL = process.env.REACT_APP_STORAGE_DOCTOR_PROFILE_PICTURE_URL;

  const { addLink } = useNav();
  // Determine admin role for inline action visibility
  let isAdmin = false;
  try {
    const info = JSON.parse(localStorage.getItem('userInfo') || '{}');
    isAdmin = info?.role === 'admin';
  } catch (_) {}

  useEffect(() => {
    addLink("اطبائنا", `${DOMAIN}control/doctor-list`);
    addLink("اضافة طبيب", `${DOMAIN}control/doctor-create`);
    addLink("الحجوزات", `${DOMAIN}control/order-list`);
    addLink('online', `${DOMAIN}control/admin/online-notes`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addLink]);

  useEffect(() => {
    document.title = "اطبائنا";
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 350);

    return () => clearTimeout(handle);
  }, [searchTerm]);

  useEffect(() => {
    // Reset page when searching
    setPage(1);
  }, [debouncedSearchTerm, perPage]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('per_page', String(perPage));
        if (debouncedSearchTerm && debouncedSearchTerm.trim()) {
          params.set('q', debouncedSearchTerm.trim());
        }

        const response = await fetch(`${API_URL}doctors/all-paginated?${params.toString()}`);
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const payload = await response.json();
        setItems(Array.isArray(payload?.data) ? payload.data : []);
        setMeta(payload?.meta || { current_page: 1, last_page: 1, per_page: perPage, total: 0 });
      } catch (e) {
        console.error("Error fetching data:", e);
        setError("فشل تحميل قائمة الأطباء");
        setItems([]);
        setMeta({ current_page: 1, last_page: 1, per_page: perPage, total: 0 });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_URL, page, perPage, debouncedSearchTerm]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4" dir="rtl">
      <header className="mb-4 text-center">
        <h1 className="display-6 mb-1">أطباؤنا</h1>
        <p className="text-muted mb-0">حسب الترتيب الأبجدي</p>
        {isAdmin && (
          <div className="mt-3 d-flex gap-2 justify-content-center">
            <a
              href={`${DOMAIN}control/admin/reviser`}
              className="btn btn-sm btn-primary"
            >
              AI Reviser (Admin)
            </a>
            <a
              href={`${DOMAIN}control/admin/metrics`}
              className="btn btn-sm btn-success"
            >
              Metrics
            </a>
          </div>
        )}
      </header>

      <div className="row g-2 align-items-center mb-3">
        <div className="col-12 col-md-8">
          <input
            type="text"
            className="form-control"
            placeholder="بحث بالاسم / اسم المستخدم / رقم الهاتف"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="col-12 col-md-4">
          <select
            className="form-select"
            value={perPage}
            onChange={(e) => setPerPage(parseInt(e.target.value, 10) || 10)}
          >
            <option value={10}>10 لكل صفحة</option>
            <option value={20}>20 لكل صفحة</option>
            <option value={50}>50 لكل صفحة</option>
            <option value={100}>100 لكل صفحة</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="table-responsive shadow-sm rounded">
        <table className="table table-hover align-middle mb-0">
          <thead className="table-light">
            <tr className="text-center">
              <th scope="col" style={{ width: 70 }}>#</th>
              <th scope="col" style={{ width: 80 }}>الصورة</th>
              <th scope="col">الاسم</th>
              <th scope="col">نبذة</th>
              <th scope="col" style={{ width: 120 }}>إجراءات</th>
            </tr>
          </thead>
          <tbody>
            {items && items.length > 0 ? (
              items.map((item, index) => {
                const placeholder = "https://via.placeholder.com/64x64?text=Dr";
                const base = DOCTOR_PROFILE_PICTURE_URL || "";
                const hasFailed = item?.doctor_id ? failedImages.has(item.doctor_id) : false;
                const computedSrc = item?.profile_picture ? `${base}${item.profile_picture}` : null;
                const imageSrc = hasFailed || !computedSrc ? placeholder : computedSrc;
                const briefText = item?.brief || "—";
                const seq = (meta?.current_page ? (meta.current_page - 1) : (page - 1)) * perPage + index + 1;
                return (
                  <tr key={item?.doctor_id ?? index}>
                    <td className="text-center fw-semibold">{seq}</td>
                    <td className="text-center">
                      <img
                        src={imageSrc}
                        alt={item?.full_name || "Doctor"}
                        style={{ width: 56, height: 56, objectFit: "cover" }}
                        className="rounded-circle border"
                        onError={() => {
                          if (item?.doctor_id && !failedImages.has(item.doctor_id)) {
                            const next = new Set(failedImages);
                            next.add(item.doctor_id);
                            setFailedImages(next);
                          }
                        }}
                      />
                    </td>
                    <td className="fw-semibold">{item?.full_name || "—"}</td>
                    <td className="text-muted">
                      {briefText && briefText.length > 90 ? `${briefText.slice(0, 90)}…` : briefText}
                    </td>
                    <td className="text-center">
                      <a
                        href={`${DOMAIN}control/doctor/${item?.doctor_id}`}
                        className="btn btn-sm btn-info"
                      >
                        مشاهدة
                      </a>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="5" className="text-center text-muted py-4">
                  لا توجد بيانات لعرضها
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3 gap-2">
        <div className="text-muted">
          الإجمالي: {meta?.total ?? 0}
        </div>

        <nav aria-label="Doctor list pagination">
          <ul className="pagination mb-0">
            <li className={`page-item ${(meta?.current_page ?? page) <= 1 ? 'disabled' : ''}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                السابق
              </button>
            </li>

            <li className="page-item disabled">
              <span className="page-link">
                صفحة {meta?.current_page ?? page} من {meta?.last_page ?? 1}
              </span>
            </li>

            <li className={`page-item ${(meta?.current_page ?? page) >= (meta?.last_page ?? 1) ? 'disabled' : ''}`}>
              <button
                type="button"
                className="page-link"
                onClick={() => setPage((p) => Math.min((meta?.last_page ?? p + 1), p + 1))}
              >
                التالي
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default DoctorList;